import {
  APIDashboardFilterExpression,
  APIFieldType,
  Operator,
  ReportColumnType,
  SupportedDashboardFilterType,
} from '@ardoq/api-types';
import type {
  DashboardFilterOperatorButtonProps,
  DashboardFilterPillProps,
  DashboardFilterPillsUIStateWithSetters,
  DashboardFilterPillValueProps,
  DashboardPillLabels,
  UnlinkedDashboardFilterPillProps,
} from '@ardoq/table-filter-bar';
import { isEmpty } from 'lodash';
import type { DashboardFilter } from '../types';
import { isReportComponentIdValueColumn } from '../utils';
import type { Locale } from '@ardoq/locale';
import { type I18nLocale, t } from '@ardoq/i18n';
import { IconName } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { getNumberFilterOperatorAndValueProps } from './propsMapper/numberFilterPropsMapper';
import { getListFilterOperatorAndValueProps } from './propsMapper/listFilterPropsMapper';
import {
  getFlatListWithSuggestionsOperatorAndValueProps,
  getHierarchicalListWithSuggestionsOperatorAndValueProps,
} from './propsMapper/listWithSuggestionsOperatorAndValueProps';
import { getDateFilterOperatorAndValueProps } from './propsMapper/dateFilterPropsMapper';
import type { GetOperatorAndValuePropsParams } from './propsMapper/utils';

const getEditLinkButtonDisabledProps = (
  anyLinkableChartsAreInaccessible: boolean,
  associatedFieldIsNotValid: boolean,
  i18nLocale: I18nLocale
): { isDisabled: boolean; extraText: string } => {
  // If any are inaccessible, prioritize this info
  if (anyLinkableChartsAreInaccessible) {
    return {
      isDisabled: true,
      extraText: t(
        i18nLocale,
        'dashboard:filter.pill.editLinkDisabledNoAccess',
        `Some charts use data sources you don't have access to`
      ),
    };
  }

  // If all accessible, just ensure field is valid
  if (associatedFieldIsNotValid) {
    return {
      isDisabled: true,
      extraText: t(
        i18nLocale,
        'dashboard:filter.pill.editLinkDisabledInvalidColumn',
        'The selected column is no longer available or has changed'
      ),
    };
  }

  return { isDisabled: false, extraText: '' };
};

export type FilterPillBuilderProps = {
  removeFilter: VoidFunction;
  enterLinkMode: VoidFunction;
  showLinkChartPopover: boolean;
  setShowLinkChartPopover: (showLinkChartPopover: boolean) => void;
  renameFilter: (newName: string) => void;
  validateFilterName: (newName: string) => string | undefined;
};

type GetDashboardFilterPillPropsParams = {
  filterPillUIState: DashboardFilterPillsUIStateWithSetters;
  dashboardFilter: DashboardFilter;
  locale: Locale;
  i18nLocale: I18nLocale;
  dashboardPillLabels?: DashboardPillLabels;
  updateDashboardFilterValues: (
    dashboardFilterId: string,
    type: SupportedDashboardFilterType,
    values: APIDashboardFilterExpression[] | null
  ) => void;
  requestDashboardFilterSuggestions: (
    parameterId: string,
    query: string
  ) => void;
  requestDashboardFilterHierarchicalSuggestions: (
    parameterId: string,
    query: string
  ) => void;
  filterCachedHierarchicalSuggestions: (
    parameterId: string,
    query: string
  ) => void;
  hasInaccessibleWidgets: boolean;
  isLoading: boolean;
} & (
  | { isInReadOnlyMode: true }
  | ({
      isInReadOnlyMode: false;
    } & FilterPillBuilderProps)
);

const getOperatorAndValueProps = (
  params: GetOperatorAndValuePropsParams
): {
  operatorProps: DashboardFilterOperatorButtonProps;
  valueProps: DashboardFilterPillValueProps;
} => {
  const { dashboardFilter } = params;

  const filterType = dashboardFilter.type;
  switch (filterType) {
    case APIFieldType.NUMBER:
      return getNumberFilterOperatorAndValueProps({
        ...params,
        dashboardFilter,
      });

    case APIFieldType.CHECKBOX:
    case APIFieldType.SELECT_MULTIPLE_LIST:
    case APIFieldType.LIST:
      return getListFilterOperatorAndValueProps({
        ...params,
        dashboardFilter,
      });
    case ReportColumnType.REFERENCE_TYPE_INCOMING:
    case ReportColumnType.REFERENCE_TYPE_OUTGOING:
      // for type checking purposes
      return !dashboardFilter.suggestionProps ||
        dashboardFilter.suggestionProps.kind === 'hierarchical'
        ? getHierarchicalListWithSuggestionsOperatorAndValueProps({
            ...params,
            dashboardFilter: {
              ...dashboardFilter,
              type: filterType,
              suggestionProps: dashboardFilter.suggestionProps,
            },
          })
        : getFlatListWithSuggestionsOperatorAndValueProps({
            ...params,
            dashboardFilter,
          });
    case APIFieldType.USER:
    case APIFieldType.TEXT:
      return getFlatListWithSuggestionsOperatorAndValueProps({
        ...params,
        dashboardFilter,
      });
    case APIFieldType.DATE_ONLY:
    case APIFieldType.DATE_TIME:
      return getDateFilterOperatorAndValueProps({
        ...params,
        dashboardFilter,
      });
    default:
      filterType satisfies never;
      throw new Error(`Unsupported filter type: ${filterType}`);
  }
};

const getLabelIcon = (
  dashboardFilterType: SupportedDashboardFilterType | 'component',
  isLoading: boolean,
  warnings: number
): IconName => {
  if (warnings) {
    return IconName.WARNING;
  } else if (isLoading) {
    return IconName.SPINNER;
  }
  switch (dashboardFilterType) {
    case 'component':
      return IconName.COMPONENT;
    case APIFieldType.LIST:
      return IconName.LIST;
    case APIFieldType.SELECT_MULTIPLE_LIST:
      return IconName.SELECT_LIST;
    case APIFieldType.USER:
      return IconName.PERSON;
    case ReportColumnType.REFERENCE_TYPE_INCOMING:
    case ReportColumnType.REFERENCE_TYPE_OUTGOING:
      return IconName.REFERENCE;
    case APIFieldType.CHECKBOX:
      return IconName.CHECKBOX_CHECKED;
    case APIFieldType.NUMBER:
      return IconName.NUMBER;
    case APIFieldType.TEXT:
      return IconName.TEXT_FORMAT;
    case APIFieldType.DATE_TIME:
    case APIFieldType.DATE_ONLY:
      return IconName.DATE;
    default:
      dashboardFilterType satisfies never;
      return IconName.FILTER_LIST;
  }
};

export const getDashboardFilterPillProps = ({
  dashboardFilter,
  updateDashboardFilterValues,
  hasInaccessibleWidgets,
  isLoading,
  requestDashboardFilterSuggestions,
  requestDashboardFilterHierarchicalSuggestions,
  filterCachedHierarchicalSuggestions,
  filterPillUIState,
  dashboardPillLabels,
  ...builderProps
}: GetDashboardFilterPillPropsParams): DashboardFilterPillProps => {
  const { operatorProps, valueProps } = getOperatorAndValueProps({
    updateDashboardFilterValues,
    hasInaccessibleWidgets,
    isLoading,
    requestDashboardFilterSuggestions,
    requestDashboardFilterHierarchicalSuggestions,
    filterCachedHierarchicalSuggestions,
    dashboardFilter,
    filterPillUIState,
    locale: builderProps.locale,
    i18nLocale: builderProps.i18nLocale,
    filterInputPlaceholder: dashboardPillLabels?.filterInputPlaceholder,
  });
  const selectedOperator =
    'operator' in dashboardFilter ? dashboardFilter.operator : Operator.EQUAL;

  const isInvalid =
    (dashboardFilter.type === APIFieldType.LIST ||
      dashboardFilter.type === APIFieldType.SELECT_MULTIPLE_LIST) &&
    isEmpty(dashboardFilter.options);

  return {
    labels: dashboardPillLabels,
    showValueButton: !(
      (dashboardFilter.type === APIFieldType.NUMBER ||
        dashboardFilter.type === APIFieldType.DATE_ONLY ||
        dashboardFilter.type === APIFieldType.DATE_TIME) &&
      (selectedOperator === Operator.IS_EMPTY ||
        selectedOperator === Operator.IS_NOT_EMPTY)
    ),
    operatorProps,
    valueProps,
    label: {
      label: dashboardFilter.name,
      icon: {
        name: getLabelIcon(
          dashboardFilter.field.name &&
            isReportComponentIdValueColumn(dashboardFilter.field.name)
            ? 'component'
            : dashboardFilter.type,
          isLoading,
          dashboardFilter.linkCounts.warnings
        ),
        color: dashboardFilter.linkCounts.warnings
          ? colors.iconWarning
          : colors.icon,
      },
      warnings: dashboardFilter.linkCounts.warnings,
      ...(builderProps.isInReadOnlyMode
        ? {
            isInReadOnlyMode: true,
          }
        : {
            isInReadOnlyMode: false,
            enterLinkModeForFilter: builderProps.enterLinkMode,
          }),
    },
    ...(!builderProps.isInReadOnlyMode
      ? {
          isInReadOnlyMode: false,
          settings: {
            filterLabel: dashboardFilter.name,
            editLinkedCharts: {
              onClick: builderProps.enterLinkMode,
              ...getEditLinkButtonDisabledProps(
                hasInaccessibleWidgets,
                isInvalid,
                builderProps.i18nLocale
              ),
            },
            removeFilter: {
              onClick: builderProps.removeFilter,
              isDisabled: hasInaccessibleWidgets,
              extraText: hasInaccessibleWidgets
                ? t(
                    builderProps.i18nLocale,
                    'dashboard:filter.pill.removeDisabledNoAccess',
                    "You can't remove this filter because some charts use data sources you don't have access to"
                  )
                : '',
            },
            renameFilter: {
              rename: builderProps.renameFilter,
              isDisabled: false,
              extraText: '',
              validate: builderProps.validateFilterName,
            },
          },
        }
      : { isInReadOnlyMode: true }),
  };
};

export const getUnlinkedDashboardFilterPillProps = ({
  name,
  enterLinkMode,
  showLinkChartPopover,
  setShowLinkChartPopover,
  renameFilter,
  removeFilter,
  validateFilterName,
  dashboardPillLabels,
}: {
  name: string;
  dashboardPillLabels?: DashboardPillLabels;
} & FilterPillBuilderProps): UnlinkedDashboardFilterPillProps => ({
  labels: dashboardPillLabels,
  label: name,
  button: {
    onClick: () => {
      enterLinkMode();
      if (showLinkChartPopover) {
        setShowLinkChartPopover(false);
      }
    },
    showLinkChartPopover: showLinkChartPopover,
    hideLinkChartPopover: () => setShowLinkChartPopover(false),
  },
  settings: {
    filterLabel: name,
    editLinkedCharts: {
      onClick: enterLinkMode,
      isDisabled: false,
    },
    removeFilter: {
      onClick: removeFilter,
      isDisabled: false,
    },
    renameFilter: {
      rename: renameFilter,
      isDisabled: false,
      validate: validateFilterName,
    },
  },
});
